import { createFileRoute } from "@tanstack/react-router";
import { RequireAuth } from "@/components/RequireAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth, type AppRole, type UserStatus } from "@/hooks/useAuth";
import { ShieldOff, Users as UsersIcon, Clock, LogIn } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/usuarios")({
  component: () => <RequireAuth><UsersPage /></RequireAuth>,
});

interface Row {
  id: string;
  email: string;
  full_name: string | null;
  status: UserStatus;
  created_at: string;
  roles: AppRole[];
  loginCount: number;
  totalMs: number;
  lastLogin: string | null;
}

function formatDuration(ms: number) {
  if (!ms || ms < 0) return "—";
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h >= 1) return `${h}h ${min}min`;
  return `${m}min`;
}

function UsersPage() {
  const { roles: myRoles } = useAuth();
  const isAdm = myRoles.includes("adm");
  const qc = useQueryClient();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["users-admin"],
    enabled: isAdm,
    queryFn: async () => {
      const [{ data: profs, error: e1 }, { data: rs, error: e2 }, { data: sess, error: e3 }] = await Promise.all([
        supabase.from("profiles").select("*").order("created_at", { ascending: false }),
        supabase.from("user_roles").select("user_id, role"),
        supabase.from("user_sessions").select("user_id, started_at, last_seen_at, ended_at"),
      ]);
      if (e1) throw e1;
      if (e2) throw e2;
      if (e3) throw e3;
      const byUser = new Map<string, AppRole[]>();
      for (const r of rs ?? []) {
        const arr = byUser.get(r.user_id) ?? [];
        arr.push(r.role as AppRole);
        byUser.set(r.user_id, arr);
      }
      const stats = new Map<string, { count: number; total: number; last: string | null }>();
      for (const s of sess ?? []) {
        const cur = stats.get(s.user_id) ?? { count: 0, total: 0, last: null };
        cur.count += 1;
        const end = s.ended_at ?? s.last_seen_at;
        const dur = new Date(end).getTime() - new Date(s.started_at).getTime();
        cur.total += Math.max(0, dur);
        if (!cur.last || s.started_at > cur.last) cur.last = s.started_at;
        stats.set(s.user_id, cur);
      }
      return (profs ?? []).map((p) => {
        const st = stats.get(p.id);
        return {
          ...p,
          roles: byUser.get(p.id) ?? [],
          loginCount: st?.count ?? 0,
          totalMs: st?.total ?? 0,
          lastLogin: st?.last ?? null,
        };
      }) as Row[];
    },
  });

  if (!isAdm) {
    return (
      <div className="max-w-xl mx-auto mt-12 p-6 bg-card border border-border rounded-lg text-center">
        <ShieldOff className="h-10 w-10 mx-auto text-warning mb-3" />
        <h2 className="text-lg font-semibold">Acesso restrito</h2>
        <p className="text-muted-foreground mt-1">Apenas administradores podem gerenciar usuários.</p>
      </div>
    );
  }

  const setStatus = async (id: string, status: UserStatus) => {
    const { error } = await supabase.from("profiles").update({ status }).eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Status atualizado"); qc.invalidateQueries({ queryKey: ["users-admin"] }); }
  };

  const setRole = async (id: string, role: AppRole) => {
    const { error: delErr } = await supabase.from("user_roles").delete().eq("user_id", id);
    if (delErr) { toast.error(delErr.message); return; }
    const { error } = await supabase.from("user_roles").insert({ user_id: id, role });
    if (error) toast.error(error.message);
    else { toast.success("Permissão atualizada"); qc.invalidateQueries({ queryKey: ["users-admin"] }); }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UsersIcon className="h-5 w-5" /> Gestão de usuários</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center text-muted-foreground py-8">Carregando...</div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary">
                  <tr className="text-left">
                    <th className="px-3 py-2">Nome</th>
                    <th className="px-3 py-2">E-mail</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Permissão</th>
                    <th className="px-3 py-2"><span className="inline-flex items-center gap-1"><LogIn className="h-3.5 w-3.5" /> Logins</span></th>
                    <th className="px-3 py-2"><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Tempo total</span></th>
                    <th className="px-3 py-2">Último login</th>
                    <th className="px-3 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id} className="border-t border-border hover:bg-muted/40">
                      <td className="px-3 py-2">{u.full_name ?? "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{u.email}</td>
                      <td className="px-3 py-2">
                        <StatusBadge status={u.status} />
                      </td>
                      <td className="px-3 py-2">
                        <Select value={u.roles[0] ?? "visualizador"} onValueChange={(v) => setRole(u.id, v as AppRole)}>
                          <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="adm">ADM</SelectItem>
                            <SelectItem value="atualizador">Atualizador</SelectItem>
                            <SelectItem value="visualizador">Visualizador</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 tabular-nums">{u.loginCount}</td>
                      <td className="px-3 py-2 tabular-nums">{formatDuration(u.totalMs)}</td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">
                        {u.lastLogin ? new Date(u.lastLogin).toLocaleString("pt-BR") : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex gap-2 justify-end">
                          {u.status !== "approved" && (
                            <Button size="sm" onClick={() => setStatus(u.id, "approved")}>Aprovar</Button>
                          )}
                          {u.status !== "rejected" && (
                            <Button size="sm" variant="outline" onClick={() => setStatus(u.id, "rejected")}>Rejeitar</Button>
                          )}
                          {u.status !== "pending" && (
                            <Button size="sm" variant="ghost" onClick={() => setStatus(u.id, "pending")}>Pendente</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: UserStatus }) {
  const cls = {
    pending: "bg-warning text-warning-foreground",
    approved: "bg-success text-success-foreground",
    rejected: "bg-destructive text-destructive-foreground",
  }[status];
  const label = { pending: "Pendente", approved: "Aprovado", rejected: "Rejeitado" }[status];
  return <span className={cn("inline-flex text-[11px] px-2 py-0.5 rounded-full font-medium", cls)}>{label}</span>;
}
